<template>
  <div class="theme-toggle">
    <div class="theme-toggle-container">
      <button
        @click="setTheme('auto')"
        class="theme-btn"
        :class="{ active: theme === 'auto' }"
        title="Follow system preference"
      >
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="4"></circle>
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"></path>
        </svg>
        <span>Auto</span>
      </button>
      
      <button
        @click="setTheme('light')"
        class="theme-btn"
        :class="{ active: theme === 'light' }"
        title="Light mode"
      >
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="5"></circle>
          <line x1="12" y1="1" x2="12" y2="3"></line>
          <line x1="12" y1="21" x2="12" y2="23"></line>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
          <line x1="1" y1="12" x2="3" y2="12"></line>
          <line x1="21" y1="12" x2="23" y2="12"></line>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
        <span>Light</span>
      </button>
      
      <button
        @click="setTheme('dark')"
        class="theme-btn"
        :class="{ active: theme === 'dark' }"
        title="Dark mode"
      >
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
        </svg>
        <span>Dark</span>
      </button>
    </div>
    
    <div class="theme-status" v-if="showStatus">
      <span class="status-text">
        <span v-if="theme === 'auto'">Following system ({{ actualTheme }})</span>
        <span v-else-if="theme === 'light'">Light mode</span>
        <span v-else>Dark mode</span>
      </span>
    </div>
  </div>
</template>

<script setup>
import { useTheme } from '@/useTheme'

const props = defineProps({
  showStatus: {
    type: Boolean,
    default: false
  },
  compact: {
    type: Boolean,
    default: false
  }
})

const { theme, actualTheme, setTheme } = useTheme()
</script>

<style scoped>
.theme-toggle {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.theme-toggle-container {
  display: flex;
  background: var(--color-background-mute);
  border-radius: 12px;
  padding: 0.25rem;
  gap: 0.25rem;
}

.theme-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.theme-btn:hover {
  background: var(--color-background-soft);
  color: var(--color-text);
}

.theme-btn.active {
  background: var(--button-primary);
  color: white;
  box-shadow: var(--shadow-sm);
}

.theme-btn .icon {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}

.theme-status {
  text-align: center;
}

.status-text {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  font-weight: 500;
}

/* Compact variant */
.theme-toggle.compact .theme-toggle-container {
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.25rem;
}

.theme-toggle.compact .theme-btn {
  justify-content: center;
  padding: 0.5rem;
}

.theme-toggle.compact .theme-btn .icon {
  width: 18px;
  height: 18px;
}

.theme-toggle.compact .theme-btn span {
  display: none;
}

/* Responsive */
@media (max-width: 480px) {
  .theme-btn {
    padding: 0.375rem 0.75rem;
    font-size: 0.8rem;
  }
  
  .theme-btn .icon {
    width: 14px;
    height: 14px;
  }
  
  .theme-btn span {
    display: none;
  }
}
</style>